import { getAudioTracks } from './assessStream';

// ASR Plex stores at most this many characters; a browser can report an arbitrarily long label.
const MAX_DEVICE_LABEL_LENGTH = 256;

/**
 * The capture settings the browser applied to the dictation microphone, serialized in snake_case
 * to match the ASR Plex start payload contract. ASR Plex stores them beside the dictation's
 * measured audio quality: noise suppression and automatic gain control change the signal it
 * measures, so its SNR cannot be read without knowing whether they were on.
 */
export type AudioCaptureSettings = {
  device_label?: string;
  auto_gain_control?: boolean;
  noise_suppression?: boolean;
  echo_cancellation?: boolean;
  sample_rate?: number;
};

/**
 * Describe the microphone track of the raw `getUserMedia` stream. It has to be that stream, not
 * the recorder's `MediaStreamDestination` copy, whose track reports none of the microphone's
 * processing.
 *
 * Reads `getSettings()` rather than the requested constraints, because a browser may decline a
 * requested constraint and report only what it applied.
 */
export const describeAudioCapture = (
  stream: Nullable<MediaStream>
): AudioCaptureSettings | undefined => {
  const tracks = getAudioTracks(stream);
  const track = tracks.find((candidate) => candidate.readyState === 'live') ?? tracks[0];
  if (track == null) {
    return undefined;
  }
  const settings = track.getSettings?.() ?? {};
  return {
    device_label: track.label ? track.label.slice(0, MAX_DEVICE_LABEL_LENGTH) : undefined,
    auto_gain_control: settings.autoGainControl,
    noise_suppression: settings.noiseSuppression,
    echo_cancellation:
      typeof settings.echoCancellation === 'boolean' ? settings.echoCancellation : undefined,
    sample_rate: settings.sampleRate,
  };
};
